import {
  BalanceUtils,
  CashInfoExtended,
  getFloatingValue,
  Loadable,
  LoadableState,
  useCurrencyExchangeRateRT,
} from '@eih-netbroker/common';
import { useMemo } from 'react';
import { Config } from '../../../shared/config/config';
import { useAccountBalance } from '../../../shared/services/useAccountBalance';
import { useAccounts } from '../../../shared/services/useAccounts';
import { useRealtimeInventories } from '../../portfolio/services/RealtimeInventoryProvider';

export const useHeaderBalance = () => {
  const { selectedAccountOrClient } = useAccounts();
  const { accountBalance, getCashInfoByCurrency, getInvestmentLoan } = useAccountBalance();
  const { realtimeInventories } = useRealtimeInventories();
  const { getCurrencyExchangeRateRealTime, currenciesExchangeRateRT } = useCurrencyExchangeRateRT();

  const investmentLoan = useMemo(
    () => getInvestmentLoan(selectedAccountOrClient),
    [getInvestmentLoan, selectedAccountOrClient],
  );

  const cashInfo = useMemo((): LoadableState<CashInfoExtended> => {
    const cashInfoByCurrency = getCashInfoByCurrency(selectedAccountOrClient);

    if (cashInfoByCurrency.hasError) {
      return Loadable.failed;
    }

    if (cashInfoByCurrency.isLoaded) {
      return BalanceUtils.getCashInfoExtended({
        cashInfoByCurrency: cashInfoByCurrency.cashInfo,
        config: Config,
        currenciesExchangeRateRT,
      });
    }

    return Loadable.loading;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- accountBalance needed here so that useMemo is triggered when accountBalance changes
  }, [getCashInfoByCurrency, selectedAccountOrClient, currenciesExchangeRateRT, accountBalance]);

  /**
   * Calculates the floating value of the account (profit/loss of the inventory items)
   */
  const floatingValue = useMemo(() => {
    return getFloatingValue({
      realtimeInventories,
      selectedAccountOrClient,
      getCurrencyExchangeRateRealTime,
      baseCurrency: Config.NetBroker.BaseCurrency,
    });
  }, [realtimeInventories, selectedAccountOrClient, getCurrencyExchangeRateRealTime]);

  const inventoriesValue = useMemo((): LoadableState<number> => {
    return BalanceUtils.getInventoriesValue({
      inventoryList: realtimeInventories,
      accountOrClient: selectedAccountOrClient,
      getCurrencyExchangeRateRealTime,
    });
  }, [realtimeInventories, selectedAccountOrClient, getCurrencyExchangeRateRealTime]);

  const accountBalanceValue = useMemo((): LoadableState<number> => {
    if (cashInfo.isLoaded && investmentLoan.isLoaded && inventoriesValue.isLoaded) {
      // investment loan is negative value
      return Loadable.loaded(inventoriesValue.value + investmentLoan.value);
    }
    return Loadable.loading;
  }, [investmentLoan, cashInfo, inventoriesValue]);

  return {
    cashInfo,
    accountBalanceValue,
    floatingValue,
  };
};
